import {Stack} from '@commercial-helios/ui/src/components/stack'
import {tokens, colors, zIndex} from '@commercial-helios/ui/theme'
import type {CategoryPreferences, Destination} from '@segment/consent-manager/types/types'
import Cookies from 'js-cookie'
import React from 'react'
import styled, {css, keyframes} from 'styled-components'

import Button from '@helios/shared/components/button'
//import LocalizedLink from '@helios/shared/components/localized-link'
import type {Props as RichTextProps} from '@helios/shared/components/rich-text'
import RichText from '@helios/shared/components/rich-text'
import Switch from '@helios/shared/components/switch-ios'
import cookieIcon from '@helios/shared/images/icons/cookies-icon.svg'
import arrowDownIcon from '@helios/shared/images/icons/navigate-down.svg'
import {Container, BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text, /*TextLink,*/ NavTextLink} from '@helios/shared/styles/typography'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

import {cookieCategoriesMapping, cookiesMap} from '../../config/cookies'

type ConsentManagerBuilderRenderProps = {
    saveConsent: (newPreferences?: boolean | CategoryPreferences, shouldReload?: boolean) => void
    setPreferences: (newPreferences: CategoryPreferences) => void
    newDestinations: Destination[]
    preferences: CategoryPreferences
    withOverlay: boolean
}

type Cookie = {
    id: string
    name: string
    category: CookieCategory['key']
}

type CookieCategory = {
    key: string
    data: {
        bannerTitle: string
        description: string
        linkText: string
        toggleText?: string
        cookies: Cookie[]
    }
}

export type CookieBannerContent = {
    title: string
    description: RichTextProps['content']
    savePreferencesText: string
    acceptCookiesText: string
    declineCookiesText: string
    customiseText: string
    cookieCategories: CookieCategory[]
}

interface Props extends ConsentManagerBuilderRenderProps {
    content: CookieBannerContent
    withOverlay: boolean
    //acceptAll: CategoryPreferences
    //denyAll: CategoryPreferences
}

const CONTENT_ID = 'cookie-banner-content-id'
const TRIGGER_ID = 'cookie-banner-expand-id'

export const CookieBanner = ({
    content,
    saveConsent,
    setPreferences,
    preferences,
    newDestinations,
    withOverlay,
}: //acceptAll,
//denyAll,
Props) => {
    const [isBannerVisible, setIsBannerVisible] = React.useState(false)
    const [show, setShow] = React.useState(false)
    const [overlayVisible, setOverlayVisible] = React.useState(true)

    const hasNewDestinations = !!newDestinations.length

    React.useEffect(() => {
        if (!Cookies.get(cookiesMap.trackingPreferences) || hasNewDestinations) {
            setIsBannerVisible(true)
            track(TrackingEvent.CookieBannerActioned, {action: 'shown'})
        }
    }, [hasNewDestinations])

    /*
    const savePreferences = (categoryPreferences: CategoryPreferences) => {
        setPreferences(categoryPreferences)
        saveConsent(categoryPreferences, false)

        setIsBannerVisible(false)
    }
    */
    const savePreferences = (cookiesAllowed: boolean) => {
        setPreferences({})
        const shouldReload = !cookiesAllowed
        saveConsent(cookiesAllowed, shouldReload)

        setIsBannerVisible(false)
    }

    if (!isBannerVisible) {
        return null
    }

    const dismissOverlay = () => {
        track(TrackingEvent.CookieBannerActioned, {
            action: 'dismissed_overlay',
        })
        setOverlayVisible(false)
    }

    return (
        <>
            {withOverlay && <Overlay onClick={dismissOverlay} $show={overlayVisible} />}
            <StyledWrapper
                color="default"
                style={{
                    animationDelay: withOverlay ? tokens.slow : '0ms',
                }}
            >
                <CollapsibleContainer $animate={show} data-testid="cookie-banner-wrapper">
                    <Info>
                        {show && (
                            <ArrowButton
                                autoFocus
                                onClick={() => {
                                    track(TrackingEvent.CookiePreferencesActioned, {
                                        action: 'closed',
                                    })
                                    setShow(false)
                                }}
                                aria-expanded={show}
                                aria-controls={CONTENT_ID}
                            >
                                <img src={arrowDownIcon} alt="" />
                            </ArrowButton>
                        )}

                        <StyledImg src={cookieIcon} alt="" />
                        <Description>
                            <Text variant="paragraph-large-medium">{content.title}</Text>
                            <RichText variant="paragraph-base" content={content.description} />
                        </Description>
                    </Info>

                    <CollapsibleSection id={CONTENT_ID} aria-labelledby={TRIGGER_ID}>
                        {show && (
                            <Content>
                                {content.cookieCategories.map(({key, data}) => (
                                    <DividedStack key={key} space={4} stretch>
                                        <Inline>
                                            <Text variant="label-base-medium">
                                                {data.bannerTitle}
                                            </Text>

                                            {data.toggleText ? (
                                                <Text variant="label-base" color="lightMuted">
                                                    {data.toggleText}
                                                </Text>
                                            ) : (
                                                <Switch
                                                    checked={Boolean(
                                                        preferences[cookieCategoriesMapping[key]]
                                                    )}
                                                    onChange={(value) =>
                                                        setPreferences({
                                                            [cookieCategoriesMapping[key]]: value,
                                                        })
                                                    }
                                                />
                                            )}
                                        </Inline>

                                        <Text variant="label-base" color="muted">
                                            {data.description}
                                        </Text>

                                        {/*<StyledTextLink
                                        variant="label-base"
                                        as={LocalizedLink}
                                        to={`/legal/cookie-policy#${key}`}
                                    >
                                        {data.linkText}
                                    </StyledTextLink>*/}
                                    </DividedStack>
                                ))}
                            </Content>
                        )}
                    </CollapsibleSection>

                    <Actions>
                        {show ? (
                            <NavTextLink
                                as="button"
                                variant="paragraph-base"
                                onClick={() => {
                                    track(TrackingEvent.CookiePreferencesActioned, {
                                        action: 'saved',
                                        cookiesAllowed: 'preferences set',
                                    })
                                    savePreferences(true)
                                }}
                            >
                                {content.savePreferencesText}
                            </NavTextLink>
                        ) : (
                            <NavTextLink
                                as="button"
                                variant="paragraph-base"
                                onClick={() => {
                                    track(TrackingEvent.CookiePreferencesActioned, {
                                        action: 'opened',
                                    })
                                    setShow(!show)
                                }}
                                id={TRIGGER_ID}
                                aria-expanded={show}
                                aria-controls={CONTENT_ID}
                            >
                                {content.customiseText}
                            </NavTextLink>
                        )}

                        <NavTextLink
                            as="button"
                            variant="paragraph-base"
                            onClick={() => {
                                track(
                                    TrackingEvent.CookiePreferencesActioned,
                                    {
                                        action: 'saved',
                                        cookiesAllowed: false,
                                    },
                                    () => savePreferences(false)
                                )
                            }}
                        >
                            {content.declineCookiesText}
                        </NavTextLink>

                        <AllowCookiesButton
                            onClick={() => {
                                track(TrackingEvent.CookiePreferencesActioned, {
                                    action: 'saved',
                                    cookiesAllowed: true,
                                })
                                savePreferences(true)
                            }}
                            data-testid="accept-cookies-button"
                        >
                            {content.acceptCookiesText}
                        </AllowCookiesButton>
                    </Actions>
                </CollapsibleContainer>
            </StyledWrapper>
        </>
    )
}

const TABLET_BP = 'sm'
const DESKTOP_BP = 'lg'

const boxShadow = css`
    box-shadow: 0 2px 8px 1px rgb(0 0 0 / 60%);
`

const slideUp = keyframes`
    from {
        transform: translate3d(0, 100%, 0);
        opacity: 0;
    }
    to {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
`

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`

const Overlay = styled.div<{$show: boolean}>`
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${zIndex.cookieBanner2};
    width: 100%;
    height: 100%;
    will-change: opacity;
    overflow: hidden;
    overscroll-behavior: contain;
    pointer-events: ${(props) => (props.$show ? 'auto' : 'none')};
    background: ${tokens.shade900lighten80};
    opacity: ${(props) => (props.$show ? 1 : 0)};
    animation: ${fadeIn} ${tokens.slowInOut};
`

const StyledWrapper = styled(BackgroundWrapper)`
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: ${zIndex.cookieBanner2};

    /* This is a temp fix until we move to the new cookie consent flow */
    z-index: 9999999999;
    width: 100%;
    max-height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    ${boxShadow};
    will-change: transform;
    opacity: 0;
    animation: ${slideUp} ${tokens.slowInOut} forwards;

    ${mq.min(DESKTOP_BP)} {
        padding-right: ${tokens.spacing40};
        padding-left: ${tokens.spacing40};
    }
`

const StyledContainer = styled(Container)`
    padding-right: 0;
    padding-left: 0;

    ${mq.max(DESKTOP_BP)} {
        & > * {
            padding-right: ${tokens.spacing24};
            padding-left: ${tokens.spacing24};
        }
    }

    ${mq.min(TABLET_BP)} {
        display: flex;
        flex-direction: column;
    }

    ${mq.min(DESKTOP_BP)} {
        column-gap: ${tokens.spacing40};
        justify-content: space-between;
    }
`

const Actions = styled.div`
    display: flex;
    padding-top: ${tokens.spacing24};
    padding-bottom: ${tokens.spacing24};
    flex-direction: column;
    gap: ${tokens.spacing16};
    align-items: center;

    ${mq.max(TABLET_BP)} {
        position: sticky;
        bottom: 0;
        width: 100%;
        background-color: #fff;
    }

    ${mq.min(TABLET_BP)} {
        flex-shrink: 0;
        flex-direction: row;
        gap: ${tokens.spacing32};
        align-self: center;
    }

    ${mq.min(DESKTOP_BP)} {
        padding-top: ${tokens.spacing32};
        padding-bottom: ${tokens.spacing32};
        align-self: flex-end;
    }
`

type CollapsibleProps = {
    $animate: boolean
}

const CollapsibleSection = styled.section`
    display: grid;
    transition: grid-template-rows ${tokens.slowOut};

    & > div {
        overflow: hidden;
    }
`

const CollapsibleContainer = styled(StyledContainer)<CollapsibleProps>`
    ${CollapsibleSection} {
        grid-template-rows: ${(props) => (props.$animate ? '1fr' : '0fr')};
    }

    ${mq.max(TABLET_BP)} {
        ${Actions} {
            ${(props) => props.$animate && boxShadow};
        }
    }

    ${mq.min(DESKTOP_BP)} {
        flex-direction: ${(props) => (props.$animate ? 'column' : 'row')};
    }
`

const Content = styled.div`
    padding-bottom: ${tokens.spacing40};

    ${mq.min(DESKTOP_BP)} {
        display: grid;
        column-gap: ${tokens.spacing40};
        grid-template-columns: 1fr 1fr;
    }
`

const DividedStack = styled(Stack)`
    padding: ${tokens.spacing20} ${tokens.spacing2};
    border-bottom: 1px solid ${colors.text.lightMuted};
`

const AllowCookiesButton = styled(Button)`
    ${mq.max(TABLET_BP)} {
        width: 100%;
    }
`

const ArrowButton = styled.button`
    display: block;
    width: 48px;
    height: 48px;
    padding: 0;
    margin-left: auto;
    cursor: pointer;
    background: none;
    border: none;

    > img {
        display: inline;
    }
`

const Info = styled.div`
    padding-top: ${tokens.spacing24};
    padding-bottom: ${tokens.spacing16};

    ${mq.min(DESKTOP_BP)} {
        display: flex;
        width: 100%;
        padding-top: ${tokens.spacing32};
        padding-bottom: ${tokens.spacing32};
        align-items: center;
        align-self: flex-start;

        ${ArrowButton} {
            order: 3;
        }
    }
`
const StyledImg = styled.img`
    ${mq.max(DESKTOP_BP)} {
        margin: 0 auto ${tokens.spacing12};
    }

    ${mq.min(DESKTOP_BP)} {
        margin: 0 ${tokens.spacing20} 0 0;
    }
`

const Description = styled.div`
    flex: 1;

    ${mq.max(DESKTOP_BP)} {
        text-align: center;
    }
`

/*
const StyledTextLink = styled(TextLink)`
    width: max-content;
    color: ${colors.text.lightMuted};
`
*/

const Inline = styled.span`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
`
